import { apiFetch } from "../../../lib/http";

export async function getSubscriptionStatus() {
    return apiFetch('/api/v1/subscription/status');
}

/**
 * Poll subscription status until a condition is met.
 * Used after checkout, cancel, or resubscribe to wait for Stripe webhook to sync.
 *
 * @param {(status: Object) => boolean} condition - Function that returns true when desired state is reached
 * @param {Object} options - Polling options
 * @param {number} options.maxAttempts - Maximum number of polling attempts (default: 10)
 * @param {number} options.intervalMs - Milliseconds between attempts (default: 1500)
 * @returns {Promise<Object>} The subscription status when condition is met
 * @throws {Error} If max attempts reached without condition being met
 */
export async function pollSubscriptionStatus(condition, options = {}) {
    const { maxAttempts = 10, intervalMs = 1500 } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const status = await getSubscriptionStatus();

        if (condition(status)) {
            return status;
        }

        // Don't sleep after the last attempt
        if (attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
    }

    throw new Error('Timeout waiting for subscription update');
}

/**
 * Create an embedded-checkout session.
 *
 * Returns `{ clientSecret }` which Stripe's <EmbeddedCheckout> uses to render
 * the payment form in-page (in Stripe's own iframe). This replaced the old
 * redirect flow, which returned a hosted-checkout `{ url }` we navigated to.
 *
 * @param {Object} args
 * @param {number|string} args.priceId - Same plan identifier the old flow sent.
 * @param {string} args.returnUrl - Where Stripe returns the user after payment.
 *   Must include the literal `{CHECKOUT_SESSION_ID}` token, which Stripe (not us)
 *   substitutes with the real session id.
 * @param {string} [args.promoCode] - Optional promotion code to auto-apply on the
 *   checkout form (e.g. "GRASPD26"). The backend resolves it to a Stripe promotion
 *   code and ignores unknown/expired ones. Omit when no promo applies.
 * @returns {Promise<{ clientSecret: string }>}
 */
export async function createCheckoutSession({ priceId, returnUrl, promoCode }) {
    // `promoCode: undefined` is dropped by JSON serialization, so the field is
    // simply absent when no promo applies.
    return apiFetch('/api/v1/subscription/checkout', {
        method: 'POST',
        body: { priceId, returnUrl, promoCode },
    });
}

export async function cancelSubscription() {
    return apiFetch('/api/v1/subscription/cancel', {
        method: 'POST',
    });
}

export async function resubscribeSubscription() {
    return apiFetch('/api/v1/subscription/resubscribe', {
        method: 'POST',
    });
}

/**
 * Switch the active subscription between monthly and yearly billing.
 * @param {'month' | 'year'} interval - The target billing interval.
 * @returns {Promise<Object>} The updated subscription status.
 */
export async function changeSubscriptionPlan(interval) {
    return apiFetch('/api/v1/subscription/change-plan', {
        method: 'POST',
        body: { interval },
    });
}